import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, GraduationCap, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import type {
  ActivityReportResponse,
  ActivityReportProvenance,
  ActivityReportSnapshot,
  ActivitySession,
  ActivitySessionTotals,
  PaginatedResponse,
} from '../../types/domain';

const dateTime = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Karachi',
});

const number = new Intl.NumberFormat('en-PK');

const counterMetricLabels: Record<ActivityReportProvenance['counter_entries'][number]['metric'], string> = {
  student_interceptions: 'Student interceptions',
  samples: 'Samples distributed',
  doctor_consultations: 'Free doctor consultations',
};

const totalCards: Array<{ key: keyof ActivitySessionTotals; label: string }> = [
  { key: 'student_interceptions', label: 'Student interceptions' },
  { key: 'samples', label: 'Samples' },
  { key: 'doctor_consultations', label: 'Doctor consultations' },
  { key: 'completed_surveys', label: 'Completed surveys' },
  { key: 'sales_units', label: 'Sales units' },
  { key: 'sales_value', label: 'Sales value (PKR)' },
];

function Totals({ totals }: { totals: ActivitySessionTotals }) {
  return <div className="activity-report-totals">{totalCards.map(({ key, label }) => (
    <article key={key}>
      <strong>{totals[key] === null ? 'Not available' : number.format(Number(totals[key]))}</strong>
      <span>{label}</span>
    </article>
  ))}</div>;
}

function ReportTables({ snapshot }: { snapshot: ActivityReportSnapshot }) {
  const surveyRows = Object.entries(snapshot.survey_answer_counts).flatMap(
    ([question, answers]) => Object.entries(answers).map(([answer, count]) => ({
      question,
      answer,
      count,
    })),
  );
  return <div className="activity-report-detail-grid">
    <section>
      <h3>SKU sales</h3>
      {snapshot.sales_by_sku.length ? <table className="data-table"><thead><tr><th>Product</th><th>Units</th><th>Value</th></tr></thead><tbody>
        {snapshot.sales_by_sku.map((row) => <tr key={row.product_id}><td><strong>{row.product_name}</strong><small>{row.sku}</small></td><td>{number.format(row.quantity)}</td><td>{row.value === null ? '—' : number.format(Number(row.value))}</td></tr>)}
      </tbody></table> : <p>No SKU sales submitted.</p>}
    </section>
    <section>
      <h3>Survey response totals</h3>
      {surveyRows.length ? <table className="data-table"><thead><tr><th>Question key</th><th>Answer</th><th>Responses</th></tr></thead><tbody>
        {surveyRows.map((row) => <tr key={`${row.question}-${row.answer}`}><td>{row.question}</td><td>{row.answer}</td><td>{number.format(row.count)}</td></tr>)}
      </tbody></table> : <p>No student surveys submitted.</p>}
    </section>
  </div>;
}

function ReportProvenance({ provenance }: { provenance: ActivityReportProvenance }) {
  const { readiness } = provenance;
  return <section className="activity-report-provenance">
    <div className="activity-report-section-heading">
      <div><h3>Certification readiness</h3><p>Reconcile synced source records before the lead certifies this report.</p></div>
      <StatusPill value={readiness.attendance_complete ? 'attendance complete' : 'attention required'} />
    </div>
    <div className="activity-readiness-grid">
      <article><strong>{readiness.checked_in_count} of {readiness.team_member_count}</strong><span>Team checked in</span></article>
      <article><strong>{readiness.checked_out_count} of {readiness.team_member_count}</strong><span>Team checked out</span></article>
      <article><strong>{number.format(provenance.counter_entries.length)}</strong><span>Counter contributions</span></article>
    </div>
    <ul className="activity-readiness-warnings">
      {readiness.warnings.map((warning) => <li key={warning}>{warning}</li>)}
    </ul>
    {provenance.site_contact.name || provenance.site_contact.phone ? <p className="activity-site-contact"><strong>Site contact:</strong> {[provenance.site_contact.name, provenance.site_contact.phone].filter(Boolean).join(' · ')}</p> : null}
    <div className="activity-report-detail-grid activity-provenance-grid">
      <section>
        <h3>Team attendance provenance</h3>
        {provenance.team.length ? <table className="data-table"><thead><tr><th>Brand ambassador</th><th>Role</th><th>Check-in</th><th>Checkout</th></tr></thead><tbody>
          {provenance.team.map((member) => <tr key={member.assignment_id}>
            <td><strong>{member.ba_name}</strong><small>{member.assignment_reference}</small></td>
            <td>{member.role === 'team' ? 'Team member' : member.role === 'lead' ? 'Team lead' : 'Backup lead'}</td>
            <td>{member.check_in ? <><strong>{dateTime.format(new Date(member.check_in.captured_at))}</strong><small>{member.check_in.reference} · {member.check_in.validation_status}</small></> : <span className="activity-missing-evidence">Missing</span>}</td>
            <td>{member.check_out ? <><strong>{dateTime.format(new Date(member.check_out.captured_at))}</strong><small>{member.check_out.reference} · {member.check_out.validation_status}</small></> : <span className="activity-missing-evidence">Missing</span>}</td>
          </tr>)}
        </tbody></table> : <p>No accepted team assignments cover this session.</p>}
      </section>
      <section>
        <h3>Counter contribution provenance</h3>
        {provenance.counter_entries.length ? <table className="data-table"><thead><tr><th>Brand ambassador</th><th>Metric</th><th>Quantity</th><th>Captured</th></tr></thead><tbody>
          {provenance.counter_entries.map((entry) => <tr key={entry.reference}>
            <td><strong>{entry.ba_name}</strong><small>{entry.reference}{entry.note ? ` · ${entry.note}` : ''}</small></td>
            <td>{counterMetricLabels[entry.metric]}</td>
            <td>{number.format(entry.quantity)}</td>
            <td>{dateTime.format(new Date(entry.captured_at))}</td>
          </tr>)}
        </tbody></table> : <p>No activity counter contributions submitted.</p>}
      </section>
    </div>
  </section>;
}

export function ActivitySessionsPage() {
  const { accessToken, selectedWorkspace } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selectedSession, setSelectedSession] = useState<ActivitySession | null>(null);
  const listPath = useMemo(() => {
    const params = new URLSearchParams({ page_size: '100', ordering: '-shift__starts_at' });
    if (search.trim()) params.set('search', search.trim());
    if (status) params.set('status', status);
    return `/activity-sessions/?${params.toString()}`;
  }, [search, status]);
  const sessions = useQuery({
    queryKey: ['activity-sessions', listPath, accessToken],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<PaginatedResponse<ActivitySession>>(
      listPath,
      accessToken ? { accessToken } : {},
    ),
  });
  const report = useQuery({
    queryKey: ['activity-session-report', selectedSession?.id, accessToken],
    enabled: Boolean(accessToken && selectedSession),
    queryFn: () => apiRequest<ActivityReportResponse>(
      `/activity-sessions/${selectedSession?.id}/report/`,
      accessToken ? { accessToken } : {},
    ),
  });
  const download = useMutation({
    mutationFn: (sessionId: string) => downloadApiFile(
      `/activity-sessions/${sessionId}/report/export/${selectedWorkspace ? `?workspace=${selectedWorkspace.id}` : ''}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const rows = sessions.data?.results ?? [];

  return <>
    <header className="page-heading">
      <div>
        <p className="eyebrow">{selectedWorkspace?.name}</p>
        <h1>Activity sessions</h1>
        <p>Review university activation totals generated from attendance, counters, surveys, and SKU sales.</p>
      </div>
      <span className="record-count"><GraduationCap size={16} /> {sessions.data?.count ?? 0} sessions</span>
    </header>

    <section className="content-card management-form activity-session-filters">
      <label>Search<input aria-label="Search activity sessions" onChange={(event) => setSearch(event.target.value)} placeholder="Campaign, university, city or team lead" value={search} /></label>
      <label>Status<select aria-label="Activity session status" onChange={(event) => setStatus(event.target.value)} value={status}><option value="">All statuses</option><option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></label>
    </section>

    <DataPanel loading={sessions.isLoading} error={sessions.isError} empty={rows.length === 0}>
      <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table activity-session-table">
        <thead><tr><th>Session</th><th>Campaign</th><th>Team lead</th><th>Activity totals</th><th>Report</th><th aria-label="Actions" /></tr></thead>
        <tbody>{rows.map((session) => <tr key={session.id}>
          <td><strong>{session.activity_site_name}</strong><small>{session.city} · {dateTime.format(new Date(session.starts_at))}</small><small>{session.reference}</small></td>
          <td>{session.campaign_name}</td>
          <td><strong>{session.lead_name}</strong><small>{session.backup_lead_name ? `Backup: ${session.backup_lead_name}` : 'No backup lead'}</small></td>
          <td><strong>{number.format(session.current_totals.student_interceptions)} interceptions · {number.format(session.current_totals.samples)} samples</strong><small>{number.format(session.current_totals.completed_surveys)} surveys · {number.format(session.current_totals.sales_units)} sales units</small></td>
          <td>{session.latest_certification ? <><StatusPill value={session.has_uncertified_changes ? 'amendment required' : 'certified'} /><small>Revision {session.latest_certification.revision}</small></> : <StatusPill value={session.status} />}</td>
          <td><button aria-label={`Open ${session.reference} report`} className="button secondary small" onClick={() => setSelectedSession(session)} type="button"><ExternalLink size={14} /> Open</button></td>
        </tr>)}</tbody>
      </table></div></section>
    </DataPanel>

    {selectedSession ? <div className="confirmation-backdrop" role="presentation">
      <section aria-labelledby="activity-report-title" aria-modal="true" className="confirmation-dialog activity-report-dialog" role="dialog">
        <button aria-label="Close activity report" className="icon-button confirmation-close" onClick={() => setSelectedSession(null)} type="button"><X size={18} /></button>
        <div className="activity-report-heading">
          <div><p className="eyebrow">{selectedSession.reference}</p><h2 id="activity-report-title">{selectedSession.activity_site_name}</h2><p>{selectedSession.campaign_name} · {selectedSession.city} · {dateTime.format(new Date(selectedSession.starts_at))}</p></div>
          <button className="button secondary" disabled={download.isPending} onClick={() => download.mutate(selectedSession.id)} type="button"><Download size={16} /> {download.isPending ? 'Preparing XLSX…' : 'Download XLSX'}</button>
        </div>
        {download.isError ? <p className="form-error">{getApiErrorMessage(download.error)}</p> : null}
        <DataPanel loading={report.isLoading} error={report.isError} empty={!report.data}>
          {report.data ? <>
            <div className="activity-certification-banner">
              {report.data.latest_certification ? <><strong>Certified revision {report.data.latest_certification.revision}</strong><span>By {report.data.latest_certification.certified_by_name} on {dateTime.format(new Date(report.data.latest_certification.certified_at))}</span>{selectedSession.has_uncertified_changes ? <em>Late data is waiting for a lead-certified amendment.</em> : null}</> : <><strong>Live generated report</strong><span>The team lead or backup lead has not certified it yet.</span></>}
            </div>
            <Totals totals={report.data.current.totals} />
            <ReportProvenance provenance={report.data.provenance} />
            <ReportTables snapshot={report.data.current} />
            {report.data.certification_history.length ? <section className="activity-certification-history"><h3>Certification history</h3>{report.data.certification_history.map((item) => <div key={item.id}><strong>Revision {item.revision} · {item.reference}</strong><span>{item.certified_by_name} · {dateTime.format(new Date(item.certified_at))}</span><small>{item.note || 'Initial certification'}</small></div>)}</section> : null}
          </> : null}
        </DataPanel>
      </section>
    </div> : null}
  </>;
}
